import * as Sentry from "@sentry/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, Copy, Check } from "lucide-react";
import { isChunkLoadErrorMessage } from "@/lib/chunk-recovery";
import { getFriendlyMessage } from "@/lib/error-utils";

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

/**
 * Check if the error is a chunk loading error (stale deploy mid-session).
 * Delegates to the shared matcher in lib/chunk-recovery so the global
 * auto-reload listener + this in-render fallback agree on what counts.
 */
function isChunkLoadError(error: Error): boolean {
  return isChunkLoadErrorMessage(error.message);
}

function ChunkErrorFallback() {
  const handleRefresh = () => {
    // Clear any service worker caches and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="flex flex-col items-center gap-4 max-w-md text-center p-6">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Update Available</h2>
        <p className="text-muted-foreground text-sm">
          A new version of TrainerPulse is available. Please refresh to get the latest updates.
        </p>
        <Button onClick={handleRefresh} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Now
        </Button>
      </div>
    </div>
  );
}

function ErrorFallback({ error, resetError }: FallbackProps) {
  // Handle chunk loading errors with a friendlier message
  if (isChunkLoadError(error)) {
    return <ChunkErrorFallback />;
  }

  // Friendly text + a short copyable code (the debug breadcrumb). The code is
  // also logged to Sentry by getFriendlyMessage's pipeline, so a user reading
  // it back to support maps to the actual event — no more raw <pre> dump.
  const friendly = getFriendlyMessage(error);
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard?.writeText(friendly.code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => { /* clipboard blocked — the code is still visible to read aloud */ },
    );
  };

  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="flex flex-col items-center gap-4 max-w-md text-center p-6">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-destructive"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold">{friendly.title}</h2>
        <p className="text-muted-foreground text-sm">
          {friendly.userMessage} If it keeps happening, send us the code below.
        </p>

        {/* Copyable debug breadcrumb — replaces the raw error.message <pre>.
            Friendly for the user, and the code maps to the Sentry event. */}
        <button
          type="button"
          onClick={copyCode}
          className="mt-1 inline-flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-1.5 font-mono text-xs text-muted-foreground transition-colors hover:bg-muted/70"
          aria-label="Copy error code"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : `code: ${friendly.code}`}
        </button>

        <div className="mt-2 flex gap-2">
          <Button onClick={resetError}>Try Again</Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} resetError={resetError} />
      )}
      showDialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
